'use client'

/**
 * Segment-level error boundary for the whole app tree. Keeps the root layout
 * mounted so a single route crashing does not blank the document.
 *
 * Errors reaching a React error boundary are not automatically reported, so
 * they are captured explicitly here.
 */

import { useEffect } from 'react'

import Link from 'next/link'

import { AlertTriangle, Home, RefreshCw } from 'lucide-react'

import * as Sentry from '@sentry/nextjs'

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  return (
    <div className="min-h-page bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          <div className="w-24 h-24 mx-auto rounded-full bg-muted flex items-center justify-center">
            <AlertTriangle className="w-12 h-12 text-muted-foreground" />
          </div>
        </div>

        <h1 className="text-3xl font-bold text-foreground mb-4">Something went wrong</h1>
        <p className="text-lg text-muted-foreground mb-8">
          This page ran into an unexpected problem. Our team has been notified.
        </p>

        <div className="space-y-4">
          <button
            onClick={() => reset()}
            className="w-full btn-primary flex items-center justify-center space-x-2 touch-manipulation min-h-[48px]"
          >
            <RefreshCw className="w-5 h-5" />
            <span>Try again</span>
          </button>

          <Link
            href="/dashboard"
            className="w-full btn-outline flex items-center justify-center space-x-2 touch-manipulation min-h-[48px]"
          >
            <Home className="w-5 h-5" />
            <span>Go to Dashboard</span>
          </Link>
        </div>

        {error.digest ? (
          <p className="mt-12 text-sm text-muted-foreground">
            Reference: <span className="font-mono">{error.digest}</span>
          </p>
        ) : null}
      </div>
    </div>
  )
}
